import { detectLanguage } from '../../utils/language';
import { formatList, formatQuestions, formatSpecifications } from './formatters';
import { useDebugStore } from '../../store/debugStore';

export class FormattingService {
  formatMessage(content: string): string {
    // Skip processing for empty content
    if (!content || content.trim() === '-' || content.trim() === '') {
      return content;
    }
    
    const language = detectLanguage(content);
    
    // Log the original content for debugging
    const debugStore = useDebugStore.getState();
    debugStore.addLog('request', {
      type: 'formatting',
      originalContent: content
    });
    
    // Clean up reference markers and other unwanted artifacts
    const cleanedContent = this.cleanContent(content);
    
    // Log the cleaned content
    debugStore.addLog('response', {
      type: 'formatting',
      cleanedContent
    });
    
    const paragraphs = cleanedContent.split('\n\n').filter(Boolean);
    
    const formattedContent = paragraphs
      .map(paragraph => this.formatParagraph(paragraph, language))
      .join('\n\n');
      
    // Log the final formatted content
    debugStore.addLog('response', {
      type: 'formatting',
      formattedContent
    });
    
    return formattedContent;
  }

  private formatParagraph(paragraph: string, language: 'nl' | 'en'): string {
    // Remove any leading bullets, dots, or whitespace at the beginning of paragraphs
    const cleanedParagraph = paragraph.replace(/^\s*[•\.\-]\s*/g, '');
    
    // Special handling for address blocks
    if (this.isAddressBlock(cleanedParagraph)) {
      return cleanedParagraph; // Preserve address blocks as-is
    }
    
    // Preserve paragraphs with links - don't modify them
    if (this.containsLinks(cleanedParagraph)) {
      // Fix any malformed links that might contain JavaScript code
      return this.fixMalformedLinks(cleanedParagraph);
    }
    
    if (cleanedParagraph.includes('- ')) {
      return formatList(cleanedParagraph, language);
    }
    
    if (cleanedParagraph.includes('?') && cleanedParagraph.includes('-')) {
      return formatQuestions(cleanedParagraph);
    }
    
    if (this.isSpecification(cleanedParagraph)) {
      return formatSpecifications(cleanedParagraph, language);
    }
    
    return cleanedParagraph;
  }

  private isSpecification(content: string): boolean {
    return content.toLowerCase().includes('specificatie') || 
           content.toLowerCase().includes('specification');
  }
  
  private isAddressBlock(content: string): boolean {
    return (
      (content.includes('Industrieweg') || 
       content.includes('Assendelft') || 
       content.includes('Nederland')) &&
      (content.includes('E-mail') || 
       content.includes('Telefoonnummer') || 
       content.includes('Website'))
    );
  }
  
  private containsLinks(content: string): boolean {
    // Check for markdown links, plain URLs, or email addresses
    return /\[.*?\]\(.*?\)/.test(content) || 
           /https?:\/\/[^\s]+/.test(content) || 
           /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(content);
  }
  
  private fixMalformedLinks(content: string): string {
    // Fix links that might contain JavaScript code
    let fixed = content;
    
    // Fix markdown links with JavaScript code
    fixed = fixed.replace(
      /\[([^\]]+)\]\(([^)]+)', '_blank'\);.*?\)/g,
      (_match, text, url) => {
        // Extract the actual URL from the malformed link
        const actualUrl = url.split("'")[0];
        return `[${text}](${actualUrl})`;
      }
    );
    
    // Fix plain URLs with JavaScript code
    fixed = fixed.replace(
      /(https?:\/\/[^\s]+)', '_blank'\);.*/g,
      (_match, url) => url
    );
    
    // Remove any bullet points at the beginning of lines
    fixed = fixed.replace(/^•\s*/gm, '');
    
    return fixed;
  }
  
  private cleanContent(content: string): string {
    // Remove reference markers like 【4:0†ECM-Contact-Advies.md】
    let cleaned = content.replace(/【[^】]+】/g, '');
    
    // Remove markdown bold markers
    cleaned = cleaned.replace(/\*\*([^*]+)\*\*/g, '$1');
    
    // Preserve line breaks in address blocks
    cleaned = cleaned.replace(/(\w+)  \n/g, '$1\n');
    
    // Fix any double line breaks that might have been created
    cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n');
    
    // Fix any trailing whitespace
    cleaned = cleaned.replace(/\s+$/gm, '');
    
    // Remove any leading bullets, dots, or whitespace at the beginning of paragraphs
    cleaned = cleaned.replace(/^\s*[•\.\-]\s*/gm, '');
    
    // Remove bullet points (•) from the text
    cleaned = cleaned.replace(/•\s*/g, '');
    
    // Fix malformed links with JavaScript code - more comprehensive pattern
    cleaned = cleaned.replace(
      /\[([^\]]+)\]\(([^)]+)', '_blank'\);.*?\)/g,
      (_match, text, url) => {
        // Extract the actual URL from the malformed link
        const actualUrl = url.split("'")[0];
        return `[${text}](${actualUrl})`;
      }
    );
    
    // Fix plain URLs with JavaScript code - more comprehensive pattern
    cleaned = cleaned.replace(
      /(https?:\/\/[^\s]+)', '_blank'\);.*/g,
      (_match, url) => url
    );
    
    return cleaned;
  }
}

export const formattingService = new FormattingService();